import React, { useCallback, useMemo, useState } from "react";
import { Avatar, Box, Button, CircularProgress, Menu, MenuItem, TextField, Typography } from "@mui/material";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

import {
  createTeamMemberIfMissing,
  fetchRecruiterUsers,
  fetchTeamMembers,
  fetchTeamRoster,
} from "../utils/teamMembers";
import { BulkActionsBarContext } from "./BulkActionsBarContext";

const PURPLE = "#9C4FFF";
const MUTED = "#667085";
const SOFT_PURPLE_HOVER_BG = "#F5F3FF";

function getInitials(value) {
  const parts = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  if (!parts.length) return "TM";
  return parts.map((part) => part[0]?.toUpperCase() || "").join("");
}

export default function AssignTeamMemberAction({
  apiBase,
  selectedCount = 0,
  disabled = false,
  label = "Assign Team Member",
  optionsMode = "recruiterUsers",
  assignmentRoleLabel = "team member",
  emptySelectionMessage = "Please select at least one item to assign a team member.",
  onAssign,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [recruiterUsers, setRecruiterUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [assigningKey, setAssigningKey] = useState("");
  const [menuError, setMenuError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const { requestConfirmation, notifySelectionRequired } = React.useContext(BulkActionsBarContext);

  const selectionMissing = !selectedCount;
  const effectiveDisabled = disabled || selectionMissing;
  const open = Boolean(anchorEl);
  const loadingLabel =
    optionsMode === "teamMembers" || optionsMode === "teamRoster"
      ? "Loading team members..."
      : "Loading recruiter users...";
  const emptyLabel =
    optionsMode === "teamMembers" || optionsMode === "teamRoster"
      ? "No team members found"
      : "No recruiter users found";
  const loadErrorLabel =
    optionsMode === "teamMembers" || optionsMode === "teamRoster"
      ? "Failed to load team members."
      : "Failed to load recruiter users.";

  const triggerSx = useMemo(
    () => ({
      minWidth: "auto",
      p: 0,
      px: 0,
      py: 0,
      borderRadius: 999,
      textTransform: "none",
      fontSize: 14,
      fontWeight: 500,
      lineHeight: 1.2,
      color: effectiveDisabled ? "#B3B8C5" : PURPLE,
      background: "transparent !important",
      backgroundColor: "transparent !important",
      border: "none !important",
      borderColor: "transparent !important",
      boxShadow: "none !important",
      justifyContent: "flex-start",
      "&:hover, &:focus, &:focus-visible, &.Mui-focusVisible, &:active": {
        background: `${SOFT_PURPLE_HOVER_BG} !important`,
        backgroundColor: `${SOFT_PURPLE_HOVER_BG} !important`,
        border: "none !important",
        borderColor: "transparent !important",
        boxShadow: "none !important",
        color: effectiveDisabled ? "#B3B8C5" : "#7C3AED",
      },
      "&.Mui-disabled": {
        color: "#B3B8C5",
        background: "transparent !important",
        backgroundColor: "transparent !important",
        border: "none !important",
        borderColor: "transparent !important",
        boxShadow: "none !important",
      },
      "& .MuiButton-endIcon": {
        ml: 0.25,
      },
    }),
    [effectiveDisabled]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setMenuError("");
    setAssigningKey("");
    setSearchTerm("");
  }, []);

  const visibleRecruiterUsers = useMemo(() => {
    const query = String(searchTerm || "").trim().toLowerCase();
    if (!query) return recruiterUsers;
    return recruiterUsers.filter((user) =>
      [user?.name, user?.email, user?.role]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [recruiterUsers, searchTerm]);

  const handleOpen = useCallback(
    async (event) => {
      if (disabled) return;
      if (selectionMissing) {
        notifySelectionRequired?.(emptySelectionMessage);
        return;
      }

      setAnchorEl(event.currentTarget);
      setMenuError("");

      if (loaded || loading) return;

      setLoading(true);
      try {
        const rows =
          optionsMode === "teamRoster"
            ? await fetchTeamRoster({ apiBase })
            : optionsMode === "teamMembers"
            ? await fetchTeamMembers({ apiBase })
            : await fetchRecruiterUsers({ apiBase });
        setRecruiterUsers(rows);
        setLoaded(true);
      } catch (error) {
        console.error(`Failed to load ${optionsMode === "recruiterUsers" ? "recruiter users" : "team members"}:`, error);
        setMenuError(error?.message || loadErrorLabel);
      } finally {
        setLoading(false);
      }
    },
    [
      apiBase,
      disabled,
      emptySelectionMessage,
      loadErrorLabel,
      loaded,
      loading,
      notifySelectionRequired,
      optionsMode,
      selectionMissing,
    ]
  );

  const handleAssign = useCallback(
    async (user) => {
      if (!user?.name || typeof onAssign !== "function") return;

      const nextKey = String(user.id ?? user.name);
      const runAssignment = async () => {
        setAssigningKey(nextKey);
        setMenuError("");

        try {
          if (optionsMode === "recruiterUsers") {
            try {
              await createTeamMemberIfMissing({
                apiBase,
                name: user.name,
              });
            } catch (syncError) {
              console.warn(
                `Unable to sync ${assignmentRoleLabel} into team members before assignment:`,
                syncError
              );
            }
          }
          await onAssign(user, { confirmedByBulkActionsBar: true });
          handleClose();
        } catch (error) {
          console.error(`Failed to assign ${assignmentRoleLabel}:`, error);
          setMenuError(error?.message || `Failed to assign ${assignmentRoleLabel}.`);
        } finally {
          setAssigningKey("");
        }
      };

      if (typeof requestConfirmation === "function") {
        handleClose();
        requestConfirmation({
          title: "Confirm Bulk Action",
          message: `Are you sure you want to assign ${user.name} as the ${assignmentRoleLabel} for ${selectedCount} selected ${selectedCount === 1 ? "item" : "items"}?`,
          confirmLabel: "Assign",
          cancelLabel: "Cancel",
          onConfirm: runAssignment,
        });
        return;
      }

      await runAssignment();
    },
    [apiBase, assignmentRoleLabel, handleClose, onAssign, optionsMode, requestConfirmation, selectedCount]
  );

  return (
    <>
      <Button
        className="bulk-action-flat"
        type="button"
        variant="text"
        disableRipple
        disabled={disabled}
        onClick={handleOpen}
        aria-disabled={effectiveDisabled ? "true" : undefined}
        endIcon={
          loading ? (
            <CircularProgress size={12} sx={{ color: "currentColor" }} />
          ) : (
            <KeyboardArrowDownRoundedIcon sx={{ fontSize: 16 }} />
          )
        }
        sx={triggerSx}
      >
        {label}
      </Button>

      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 250,
            borderRadius: 2.5,
            boxShadow: "0 16px 40px rgba(15,23,42,0.14)",
          },
        }}
      >
        {!loading && !menuError && recruiterUsers.length ? (
          <MenuItem disableRipple disableGutters sx={{ px: 1, py: 0.75, cursor: "default", "&:hover": { backgroundColor: "transparent" } }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search recruiters..."
              inputProps={{ "aria-label": "Search recruiters" }}
              onKeyDown={(event) => event.stopPropagation()}
            />
          </MenuItem>
        ) : null}

        {loading ? (
          <MenuItem disabled sx={{ gap: 1.25, minHeight: 42 }}>
            <CircularProgress size={16} />
            {loadingLabel}
          </MenuItem>
        ) : null}

        {!loading && menuError ? (
          <MenuItem disabled sx={{ whiteSpace: "normal", color: "#F04438", maxWidth: 250 }}>
            {menuError}
          </MenuItem>
        ) : null}

        {!loading && !menuError && !recruiterUsers.length ? (
          <MenuItem disabled sx={{ color: MUTED }}>
            {emptyLabel}
          </MenuItem>
        ) : null}

        {!loading && !menuError && recruiterUsers.length && !visibleRecruiterUsers.length ? (
          <MenuItem disabled sx={{ color: MUTED }}>
            No recruiters match your search.
          </MenuItem>
        ) : null}

        {!loading && !menuError
          ? visibleRecruiterUsers.map((user) => {
              const itemKey = String(user.id ?? user.name);
              const isAssigning = assigningKey === itemKey;

              return (
                <MenuItem
                  key={itemKey}
                  onClick={() => handleAssign(user)}
                  disabled={Boolean(assigningKey)}
                  sx={{ gap: 1.25, py: 1, minHeight: 44 }}
                >
                  <Avatar
                    src={user.avatar_url || undefined}
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: 11,
                      fontWeight: 700,
                      bgcolor: user.avatar_url ? "transparent" : "#F5F3FF",
                      color: PURPLE,
                    }}
                  >
                    {!user.avatar_url ? getInitials(user.name) : null}
                  </Avatar>

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#344054",
                        fontFamily: "Be Vietnam Pro",
                      }}
                    >
                      {user.name}
                    </Typography>
                    {user.role ? (
                      <Typography noWrap sx={{ fontSize: 11.5, lineHeight: 1.25, color: MUTED }}>
                        {user.role}
                      </Typography>
                    ) : null}
                  </Box>

                  {isAssigning ? <CircularProgress size={14} /> : null}
                </MenuItem>
              );
            })
          : null}
      </Menu>
    </>
  );
}
